import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import useSelectedProperty from '../../../../../hooks/useSelectedProperty';
import { get } from '../../../../../utils/api';

export function useUnlockedLeasePacketSection({
  intl,
  leaseId,
  isLeaseLocked = false,
  isReadOnly = false,
  updateAddendums,
  resetAddendums,
  addendums: globalAddendums,
  storedLeaseId,
  isSelectionReady = false,
  draftScope,
  isCommercial,
  isAffordable,
}) {
  const selectedProperty = useSelectedProperty();
  const { data: leaseDocResponse, isLoading: isLeaseDocLoading } = useQuery(
    ['leaseDoc', selectedProperty.organizationId, selectedProperty.id],
    () =>
      get(
        `/${selectedProperty.organizationId}/centralized-documents/leases/${selectedProperty.id}`,
      ),
    {
      refetchOnWindowFocus: false,
    },
  );

  const unitType = isAffordable
    ? 'Affordable'
    : isCommercial
    ? 'Commercial'
    : 'Conventional';

  const {
    addendums,
    onAddendumChange,
    isLoading: isAddendumsLoading,
  } = useAddendumsForUnlockedLease({
    organizationId: selectedProperty.organizationId,
    propertyId: selectedProperty.id,
    addendums: globalAddendums,
    updateAddendums,
    resetAddendums,
    storedLeaseId,
    isSelectionReady,
    leaseId,
    draftScope,
    unitType,
  });

  const isLoading = isLeaseDocLoading || isAddendumsLoading;

  return {
    intl,
    // The library section uses isLeaseLocked only to disable the optional addendum checkboxes.
    isLeaseLocked: isLeaseLocked || isReadOnly,
    addendums,
    isLoading,
    onAddendumChange,
    leaseDocName: leaseDocResponse?.name ?? 'Lease',
  };
}

function useAddendumsForUnlockedLease({
  organizationId,
  propertyId,
  addendums,
  updateAddendums,
  resetAddendums,
  storedLeaseId,
  isSelectionReady,
  leaseId,
  draftScope,
  unitType,
}) {
  // Each lease starts from its own reads: a selection left by another lease, or by an earlier
  // visit to this lease, is never edited or saved. Two new leases share the id null and the
  // profile can stay mounted between them, so a profile change (draftScope) resets too. A provider
  // change keeps the packet mounted on the same lease, so a live draft survives it. Declared before
  // the load effect, so a load that is already cached runs after the reset.
  const hasLoadedSession = useRef(false);
  useEffect(() => {
    hasLoadedSession.current = false;
    resetAddendums();
    return () => resetAddendums();
  }, [leaseId, draftScope, resetAddendums]);

  const addendumsQuery = useQuery(
    ['addendums', organizationId, propertyId],
    () =>
      get(
        `/${organizationId}/centralized-documents/lease-addendums/property/${propertyId}?unitType=${unitType}`,
      ),
    {
      refetchOnWindowFocus: false,
    },
  );
  const selectedAddendumsQuery = useQuery(
    ['selectedAddendums', organizationId, propertyId, leaseId],
    () =>
      get(
        `/${organizationId}/centralized-documents/lease-addendums/lease/${leaseId}`,
      ),
    {
      refetchOnWindowFocus: false,
      enabled: !!leaseId,
    },
  );
  const isLoading =
    addendumsQuery.isLoading || (leaseId && selectedAddendumsQuery.isLoading);
  // A failed read stored here would save as "nothing selected" and wipe the saved addenda, with no
  // sign of it while the packet is hidden. Without a store write, a save omits optionalAddendums.
  const hasFailedRead = Boolean(
    addendumsQuery.isError || (leaseId && selectedAddendumsQuery.isError),
  );
  // A reopened lease shows cached reads while they refresh. Load only from reads that finished in
  // this session, and only once, so a later refresh never overwrites the user's edits.
  const isRefreshing = Boolean(
    addendumsQuery.isFetching || (leaseId && selectedAddendumsQuery.isFetching),
  );

  useEffect(() => {
    if (hasLoadedSession.current || isLoading || isRefreshing || hasFailedRead)
      return;
    hasLoadedSession.current = true;

    updateAddendums(
      addendumsQuery.data?.map((document) => ({
        id: document.leaseAddendum.id,
        name: document.name,
        isRequired: document.leaseAddendum.isRequired,
        isSelected:
          selectedAddendumsQuery.data?.find(
            (selectedAddendumDoc) =>
              selectedAddendumDoc.leaseAddendum.id ===
              document.leaseAddendum.id,
          ) !== undefined,
      })),
      leaseId,
    );
  }, [
    addendumsQuery.data,
    selectedAddendumsQuery.data,
    isLoading,
    isRefreshing,
    hasFailedRead,
    updateAddendums,
    leaseId,
    draftScope,
  ]);

  const ownsSelection =
    isSelectionReady && (storedLeaseId ?? null) === (leaseId ?? null);

  const onAddendumChange = useCallback(
    (id) => {
      if (!ownsSelection) return;
      updateAddendums(
        addendums.map((a) => {
          if (a.id === id)
            return {
              ...a,
              isSelected: !!!a.isSelected,
            };
          return a;
        }),
        leaseId,
      );
    },
    [addendums, updateAddendums, leaseId, ownsSelection],
  );

  return {
    addendums,
    onAddendumChange,
    isLoading,
  };
}

export function useLockedLeasePacketSection({
  intl,
  leaseId,
  isLeaseLocked = false,
}) {
  const selectedProperty = useSelectedProperty();
  const { data: leasePacketSnapshot, isLoading: isSnapshotLoading } = useQuery(
    [
      'leasePacketSnapshot',
      selectedProperty.organizationId,
      selectedProperty.id,
    ],
    () =>
      get(
        `/${selectedProperty.organizationId}/${selectedProperty.id}/leasePacketSnapshots/${leaseId}`,
      ),
    {
      refetchOnWindowFocus: false,
      enabled: !!leaseId,
    },
  );

  const isLoading = isSnapshotLoading;

  const addendums =
    leasePacketSnapshot?.leaseAddendumDocs?.map((addendumDoc) => ({
      id: addendumDoc.key,
      name: addendumDoc.name,
      isRequired: addendumDoc.isRequired,
      isSelected: addendumDoc.isSelected,
    })) ?? [];

  return {
    intl,
    isLeaseLocked,
    addendums,
    isLoading,
    onAddendumChange: () => {},
    leaseDocName: leasePacketSnapshot?.leaseDoc?.name ?? 'Lease',
  };
}
